import { useLocation, Navigate } from "react-router-dom";
import { redirectMap } from "@/lib/redirectMap";
import type { ReactNode } from "react";

/**
 * Single component that handles all legacy/SEO redirects via a lookup map.
 * Replaces ~490 individual <Route> elements to reduce React Router overhead.
 * 
 * Must be placed INSIDE <Routes> as a catch-pattern or OUTSIDE as a pre-check.
 * We use it outside Routes, checking pathname against the map before Routes renders.
 */
const normalizePath = (path: string) => {
  if (!path) return "/";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.length > 1 && normalized.endsWith("/") ? normalized.slice(0, -1) : normalized;
};

const resolveRedirectTarget = (pathname: string) => {
  let target = redirectMap[pathname] || redirectMap[normalizePath(pathname)];
  if (!target) return null;

  const seen = new Set([normalizePath(pathname)]);
  while (target && redirectMap[normalizePath(target)] && !seen.has(normalizePath(target))) {
    seen.add(normalizePath(target));
    target = redirectMap[normalizePath(target)];
  }

  return target;
};

export const RedirectHandler = ({ children }: { children?: ReactNode }) => {
  const location = useLocation();
  const target = resolveRedirectTarget(location.pathname);
  
  if (target) {
    return <Navigate to={target} replace />;
  }
  
  return <>{children ?? null}</>;
};
